import { globalEmitter, emitterProps } from 'helpers/emitter-event'
import { QuantityRule } from '@/components/helpers/cart.types'
import { cart } from '@/components/helpers/cart'
import { ShopifySectionHelper } from '../helpers/section-render'

export class QuantityInput extends HTMLElement {
  private quantity: number = 1
  private inventoryQuantity: number | null = null
  private quantityRule: QuantityRule | null = null
  private error: string | null = null
  private isLoading: boolean = false
  private errorTimeout: NodeJS.Timeout | null = null
  private line: number | null = null
  private isRemoving: boolean = false

  // Elements
  private minusButton: HTMLElement | null = null
  private plusButton: HTMLElement | null = null
  private quantityInput: HTMLInputElement | null = null
  private errorDisplay: HTMLElement | null = null
  private removeButton: HTMLElement | null = null

  constructor() {
    super()
    this.handleQuantityChange = this.handleQuantityChange.bind(this)
    this.handleRemoveClick = this.handleRemoveClick.bind(this)
  }

  static get observedAttributes() {
    return ['inventory-quantity', 'quantity', 'quantity-rule', 'line']
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return

    switch (name) {
      case 'inventory-quantity':
        this.inventoryQuantity = newValue ? parseInt(newValue) : null
        break
      case 'quantity':
        this.quantity = newValue ? parseInt(newValue) : 1
        this.updateQuantityDisplay()
        break
      case 'quantity-rule':
        try {
          this.quantityRule = newValue ? JSON.parse(newValue) : null
        } catch (e) {
          console.error('Invalid quantity rule format:', e)
        }
        break
      case 'line':
        this.line = newValue ? parseInt(newValue) : null
        break
    }
  }

  connectedCallback() {
    this.setupElements()
    this.addEventListeners()
    this.updateButtonStates()
  }

  disconnectedCallback() {
    this.removeEventListeners()
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout)
    }
  }

  private setupElements() {
    this.minusButton = this.querySelector('[data-quantity-minus]')
    this.plusButton = this.querySelector('[data-quantity-plus]')
    this.quantityInput = this.querySelector('[data-quantity-input]')
    this.errorDisplay = this.querySelector('[data-quantity-error]')
    this.removeButton = this.querySelector('[data-quantity-remove]')

    // Set initial quantity if provided via input
    if (this.quantityInput && this.quantityInput.value) {
      this.quantity = parseInt(this.quantityInput.value) || 1
    }
  }

  private addEventListeners() {
    this.minusButton?.addEventListener('click', (e) => this.handleQuantityChange(e, 'minus'))
    this.plusButton?.addEventListener('click', (e) => this.handleQuantityChange(e, 'plus'))
    this.quantityInput?.addEventListener('change', this.handleInputChange.bind(this))
    this.removeButton?.addEventListener('click', this.handleRemoveClick)
  }

  private removeEventListeners() {
    this.minusButton?.removeEventListener('click', (e) => this.handleQuantityChange(e, 'minus'))
    this.plusButton?.removeEventListener('click', (e) => this.handleQuantityChange(e, 'plus'))
    this.quantityInput?.removeEventListener('change', this.handleInputChange.bind(this))
    this.removeButton?.removeEventListener('click', this.handleRemoveClick)
  }

  private handleInputChange(e: Event) {
    const input = e.target as HTMLInputElement
    const newQuantity = parseInt(input.value)
    if (!isNaN(newQuantity)) {
      this.handleQuantityUpdate(newQuantity)
    }
  }

  private validateQuantity(newQuantity: number): boolean {
    // Check inventory limit
    if (this.inventoryQuantity !== null && newQuantity > this.inventoryQuantity) {
      this.setError('Maximum quantity reached')
      return false
    }

    // Check quantity rules if they exist
    if (this.quantityRule) {
      if (newQuantity < this.quantityRule.min) {
        this.setError(`Minimum quantity is ${this.quantityRule.min}`)
        return false
      }
      if (this.quantityRule.max && newQuantity > this.quantityRule.max) {
        this.setError(`Maximum quantity is ${this.quantityRule.max}`)
        return false
      }
    }

    return true
  }

  private async handleQuantityChange(e: Event, action: 'plus' | 'minus') {
    e.preventDefault()
    const newQuantity = this.calculateNewQuantity(action)
    await this.handleQuantityUpdate(newQuantity)
  }

  private async handleRemoveClick(e: Event) {
    e.preventDefault()
    e.stopPropagation()

    if (this.isRemoving) {
      return
    }

    if (this.hasCustomisationGroupId()) {
      await this.removeCustomisationGroup()
    } else {
      await this.removeCurrentItem()
    }
  }

  private async removeCurrentItem() {
    if (!this.line || !window.Cart) return

    try {
      this.isRemoving = true
      this.setLoading(true)

      await window.Cart.changeLineItem(
        {
          line: this.line,
          quantity: 0
        },
        { silent: true }
      )

      await ShopifySectionHelper.fetchAndUpdateSection({
        sectionId: 'cart-main',
        fetchSelectors: ['#cart-items', '#cart-summary'],
        targetSelectors: ['#cart-items', '#cart-summary']
      })
    } catch (error) {
      if (error instanceof Error) {
        globalEmitter.emit(emitterProps.notice.global, {
          type: 'error',
          content: error.message
        })
      }
    } finally {
      this.setLoading(false)
      this.isRemoving = false
    }
  }

  private async handleQuantityUpdate(newQuantity: number) {
    try {
      this.setLoading(true)

      if (!this.validateQuantity(newQuantity)) {
        this.setLoading(false)
        return
      }

      if (this.hasCustomisationGroupId()) {
        // Handle customisation group quantity changes
        if (newQuantity <= 0) {
          // Remove entire group when quantity goes to 0
          await this.removeCustomisationGroup()
        } else {
          // Update all items in the group to the same quantity
          await this.updateCustomisationGroupQuantity(newQuantity)
        }
      } else if (this.line && cart) {
        // Normal single item quantity update
        const data = await cart.changeLineItem(
          {
            line: this.line,
            quantity: newQuantity
          },
          { silent: true }
        )

        if (data.error) {
          this.setError(data.error)
          return
        }

        const changeType = data.items_added.length > 0 ? 'plus' : data.items_removed.length > 0 ? 'minus' : null
        const changedItem = data.items_added.length > 0 ? data.items_added[0] : data.items_removed.length > 0 ? data.items_removed[0] : null
        if (changeType && changedItem) {
          this.quantity = this.quantity + (changeType === 'plus' ? changedItem.quantity : -changedItem.quantity)
        }
      }
      await ShopifySectionHelper.fetchAndUpdateSection({
        sectionId: 'cart-main',
        fetchSelectors: ['#cart-items', '#cart-summary'],
        targetSelectors: ['#cart-items', '#cart-summary']
      })

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('quantity-changed', {
        detail: { quantity: newQuantity },
        bubbles: true
      }))
    } catch (error) {
      if (error instanceof Error) {
        globalEmitter.emit(emitterProps.notice.global, {
          type: 'error',
          content: error.message
        })
      }
    } finally {
      this.setLoading(false)
    }
  }

  private calculateNewQuantity(action: 'plus' | 'minus'): number {
    switch (action) {
      case 'plus':
        return this.quantity + 1
      case 'minus':
        return this.quantity - 1
      default:
        return this.quantity
    }
  }

  private updateButtonStates() {
    // Disable minus button if at minimum
    if (this.minusButton) {
      const isAtMin = this.quantity <= (this.quantityRule?.min || 0)
      this.minusButton.toggleAttribute('disabled', isAtMin)
    }

    // Disable plus button if at maximum inventory
    if (this.plusButton) {
      const isAtMax = this.inventoryQuantity !== null && this.quantity >= this.inventoryQuantity
      this.plusButton.toggleAttribute('disabled', isAtMax)
    }
  }

  private setError(message: string) {
    this.error = message
    if (this.errorDisplay) {
      this.errorDisplay.textContent = message
      this.errorDisplay.classList.remove('opacity-0', 'max-h-0')
      this.errorDisplay.classList.add('opacity-100', 'max-h-[18px]')
    }

    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout)
    }
    this.errorTimeout = setTimeout(() => this.clearError(), 5000)
  }

  private clearError() {
    this.error = null
    if (this.errorDisplay) {
      this.errorDisplay.classList.add('opacity-0', 'max-h-0')
      this.errorDisplay.classList.remove('opacity-100', 'max-h-[18px]')
    }
  }

  private setLoading(loading: boolean) {
    this.isLoading = loading
    if (loading) {
      this.classList.add('opacity-45')
    } else {
      this.classList.remove('opacity-45')
    }
  }

  private updateQuantityDisplay() {
    if (this.quantityInput) {
      this.quantityInput.value = this.quantity.toString()
    }
  }

  private hasCustomisationGroupId(): boolean {
    if (!window.Cart?.value?.items) return false

    const currentItem = window.Cart.value.items.find(item =>
      item.variant_id.toString() === this.getAttribute('variant-id')
    )

    return Boolean(currentItem?.properties?._customisationGroupId)
  }

  private async removeCustomisationGroup() {
    if (!window.Cart?.value?.items) return

    try {
      this.isRemoving = true
      this.setLoading(true)

      const currentItem = window.Cart.value.items.find(item =>
        item.variant_id.toString() === this.getAttribute('variant-id')
      )

      const groupId = currentItem?.properties?._customisationGroupId

      if (!groupId) {
        await this.removeCurrentItem()
        return
      }

      const relatedItems = window.Cart.value.items.filter(cartItem =>
        cartItem.properties?._customisationGroupId === groupId
      )

      const sortedItems = relatedItems.sort((a, b) => {
        const aIndex = window.Cart?.value?.items?.findIndex(item => item.variant_id === a.variant_id) ?? -1
        const bIndex = window.Cart?.value?.items?.findIndex(item => item.variant_id === b.variant_id) ?? -1
        return bIndex - aIndex
      })

      for (const relatedItem of sortedItems) {
        const itemLine = window.Cart.value.items.findIndex(item =>
          item.variant_id === relatedItem.variant_id
        ) + 1

        if (itemLine > 0) {
          try {
            await window.Cart.changeLineItem(
              {
                line: itemLine,
                quantity: 0
              },
              { silent: true }
            )
          } catch (error) {
            console.error('Error removing item at line', itemLine, ':', error)
          }
        }
      }

      await ShopifySectionHelper.fetchAndUpdateSection({
        sectionId: 'cart-main',
        fetchSelectors: ['#cart-items', '#cart-summary'],
        targetSelectors: ['#cart-items', '#cart-summary']
      })
    } catch (error) {
      if (error instanceof Error) {
        globalEmitter.emit(emitterProps.notice.global, {
          type: 'error',
          content: error.message
        })
      }
    } finally {
      this.setLoading(false)
      this.isRemoving = false
    }
  }

  private async updateCustomisationGroupQuantity(newQuantity: number) {
    if (!window.Cart?.value?.items) return

    try {
      this.isRemoving = true // Reusing isRemoving for now, might need a different flag
      this.setLoading(true)

      const currentItem = window.Cart.value.items.find(item =>
        item.variant_id.toString() === this.getAttribute('variant-id')
      )

      const groupId = currentItem?.properties?._customisationGroupId

      if (!groupId) {
        await this.removeCurrentItem() // Fallback to removing if groupId is not found
        return
      }

      const relatedItems = window.Cart.value.items.filter(cartItem =>
        cartItem.properties?._customisationGroupId === groupId
      )

      for (const relatedItem of relatedItems) {
        const itemLine = window.Cart.value.items.findIndex(item =>
          item.variant_id === relatedItem.variant_id
        ) + 1

        if (itemLine > 0) {
          try {
            await window.Cart.changeLineItem(
              {
                line: itemLine,
                quantity: newQuantity
              },
              { silent: true }
            )
          } catch (error) {
            console.error('Error updating item at line', itemLine, ':', error)
          }
        }
      }

      await ShopifySectionHelper.fetchAndUpdateSection({
        sectionId: 'cart-main',
        fetchSelectors: ['#cart-items', '#cart-summary'],
        targetSelectors: ['#cart-items', '#cart-summary']
      })
    } catch (error) {
      if (error instanceof Error) {
        globalEmitter.emit(emitterProps.notice.global, {
          type: 'error',
          content: error.message
        })
      }
    } finally {
      this.setLoading(false)
      this.isRemoving = false
    }
  }
}
